import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError, getJson, patchJson, postJson } from '@/api/client'
import type {
  ChatMessage,
  LiveReplyCandidate,
  LiveReplyConversationResponse,
  LiveReplyDraftBatchResponse,
  LiveReplyDraftResponse,
  LiveReplyHistoryEntry,
  LiveReplyHumanRepliedDetail,
  LiveReplySentDetail,
  LiveReplySentEntry,
  LiveReplySendResponse,
  LiveReplyServedChatDetail,
  LiveReplyServedChatSummary,
  LiveReplyVerificationStatus,
} from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { AppShell } from '@/ui/AppShell'
import { TextArea } from '@/ui/TextArea'
import { toast } from '@/ui/Toast'

type WorkbenchTab = 'console' | 'sent' | 'served'
type LogLineKind = 'info' | 'ok' | 'warn' | 'error'

interface LogLine {
  kind: LogLineKind
  text: string
}

interface ContextDebug {
  source?: string
  intent?: string
  resolution_status?: string
  context_summary?: string
  discovered_message_count?: number
  total_examined_message_count?: number
  tool_trace_summary?: Record<string, unknown>
  tool_trace?: unknown[]
  [key: string]: unknown
}

const SOLAR = {
  base03: '#002b36',
  base02: '#073642',
  base01: '#586e75',
  base00: '#657b83',
  base0: '#839496',
  base1: '#93a1a1',
  base2: '#eee8d5',
  yellow: '#b58900',
  orange: '#cb4b16',
  red: '#dc322f',
  blue: '#268bd2',
  cyan: '#2aa198',
  green: '#859900',
}

const formatTs = (value: string | null) => (value ? new Date(value).toLocaleString() : '-')
const short = (value: string | null | undefined, limit = 140) => {
  const clean = (value || '').replace(/\s+/g, ' ').trim()
  return clean.length > limit ? `${clean.slice(0, limit - 1)}…` : clean
}
const formatMoney = (value: number | null | undefined) =>
  value == null ? '-' : `$${Number(value).toFixed(4)}`

const latestCustomerMessage = (messages: ChatMessage[] | undefined) =>
  [...(messages || [])].reverse().find((message) => message.role === 'customer') || null

const verificationColor = (status: LiveReplyVerificationStatus | null | undefined) => {
  if (status === 'approved') return SOLAR.green
  if (status === 'needs_review') return SOLAR.yellow
  if (status === 'bad') return SOLAR.red
  return SOLAR.blue
}

function normalizeDraftBatch(
  data: LiveReplyDraftBatchResponse | LiveReplyDraftResponse,
): LiveReplyDraftBatchResponse {
  if ('drafts' in data && Array.isArray(data.drafts)) return data
  const draft = data as LiveReplyDraftResponse
  return {
    draft_group_id: draft.draft_group_id || `single-${draft.live_reply_id}`,
    customer_id: draft.customer_id,
    anchor_message_id: draft.anchor_message_id,
    customer_text: draft.customer_text,
    pending_intents: [
      {
        intent_id: draft.intent_id || 'intent-1',
        intent_index: draft.intent_index || 0,
        kind: 'latest_customer_message',
        text: draft.customer_text,
        message_ids: draft.anchor_message_id != null ? [draft.anchor_message_id] : [],
        requires_ad_context: false,
        order_placement_allowed: false,
      },
    ],
    drafts: [draft],
    context_discovery: draft.context_discovery,
  }
}

function CommandButton({
  children,
  onClick,
  disabled = false,
  tone = 'default',
}: {
  children: string
  onClick: () => void
  disabled?: boolean
  tone?: 'default' | 'ok' | 'danger'
}) {
  const color = tone === 'ok' ? SOLAR.green : tone === 'danger' ? SOLAR.red : SOLAR.cyan
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="border px-3 py-2 text-xs font-mono uppercase tracking-[0.08em] disabled:opacity-40"
      style={{ borderColor: color, color, background: 'transparent', borderRadius: 2 }}
    >
      {children}
    </button>
  )
}

function StatusChip({ children, color }: { children: string; color: string }) {
  return (
    <span
      className="inline-flex border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em]"
      style={{ borderColor: color, color, borderRadius: 2 }}
    >
      {children}
    </span>
  )
}

function TerminalLine({ label, children, color = SOLAR.base1 }: { label: string; children: ReactNode; color?: string }) {
  return (
    <div className="grid grid-cols-[70px_minmax(0,1fr)] gap-3 font-mono text-sm leading-relaxed">
      <div className="text-right" style={{ color }}>{label}</div>
      <div className="min-w-0 whitespace-pre-wrap" style={{ color: SOLAR.base2 }}>{children}</div>
    </div>
  )
}

function Transcript({ messages }: { messages: ChatMessage[] }) {
  if (messages.length === 0) {
    return <TerminalLine label="$">no messages loaded</TerminalLine>
  }
  return (
    <div className="space-y-2">
      {messages.map((msg) => (
        <TerminalLine
          key={msg.message_id}
          label={msg.role === 'customer' ? 'CUST' : 'AGENT'}
          color={msg.role === 'customer' ? SOLAR.cyan : SOLAR.green}
        >
          <span style={{ color: SOLAR.base01 }}>#{msg.message_id} {formatTs(msg.created_at)} </span>
          {msg.body || (msg.attachments.length > 0 ? `[${msg.attachments.length} attachment(s)]` : '[empty]')}
        </TerminalLine>
      ))}
    </div>
  )
}

export function LiveReplyPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<WorkbenchTab>('console')
  const [error, setError] = useState<string | null>(null)
  const [logs, setLogs] = useState<LogLine[]>([{ kind: 'info', text: 'agent workbench booted' }])

  const [candidates, setCandidates] = useState<LiveReplyCandidate[]>([])
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null)
  const [conversation, setConversation] = useState<LiveReplyConversationResponse | null>(null)
  const [draftBatch, setDraftBatch] = useState<LiveReplyDraftBatchResponse | null>(null)
  const [editedTexts, setEditedTexts] = useState<Record<number, string>>({})
  const [history, setHistory] = useState<LiveReplyHistoryEntry[]>([])
  const [filterChannel, setFilterChannel] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [generating, setGenerating] = useState(false)
  const [sendingDraftId, setSendingDraftId] = useState<number | null>(null)
  const [attended, setAttended] = useState<LiveReplyHumanRepliedDetail | null>(null)
  const [lastDraftedAnchorId, setLastDraftedAnchorId] = useState<number | null>(null)

  const [sentRows, setSentRows] = useState<LiveReplySentEntry[]>([])
  const [selectedSentId, setSelectedSentId] = useState<number | null>(null)
  const [sentDetail, setSentDetail] = useState<LiveReplySentDetail | null>(null)
  const [sentFilter, setSentFilter] = useState('')

  const [servedRows, setServedRows] = useState<LiveReplyServedChatSummary[]>([])
  const [selectedServedCustomerId, setSelectedServedCustomerId] = useState<number | null>(null)
  const [servedDetail, setServedDetail] = useState<LiveReplyServedChatDetail | null>(null)
  const [servedQuery, setServedQuery] = useState('')
  const [servedDraftBatch, setServedDraftBatch] = useState<LiveReplyDraftBatchResponse | null>(null)
  const [servedEditedTexts, setServedEditedTexts] = useState<Record<number, string>>({})
  const [servedGenerating, setServedGenerating] = useState(false)
  const [servedSendingDraftId, setServedSendingDraftId] = useState<number | null>(null)
  const [servedAttended, setServedAttended] = useState<LiveReplyHumanRepliedDetail | null>(null)

  function log(kind: LogLineKind, text: string) {
    setLogs((rows) => [...rows.slice(-8), { kind, text }])
  }

  function extractHumanReplied(err: unknown): LiveReplyHumanRepliedDetail | null {
    if (err instanceof ApiError && err.status === 409) {
      const detail = err.detailData as Partial<LiveReplyHumanRepliedDetail> | undefined
      if (detail?.code === 'human_already_replied' && detail.agent_reply) {
        return detail as LiveReplyHumanRepliedDetail
      }
    }
    return null
  }

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/', { replace: true })
      return
    }
    void loadCandidates()
    void loadSentRows()
    void loadServedRows()
  }, [user, navigate])

  useEffect(() => {
    if (selectedCustomerId == null) {
      setConversation(null)
      setDraftBatch(null)
      setEditedTexts({})
      setHistory([])
      setAttended(null)
      setLastDraftedAnchorId(null)
      return
    }
    setAttended(null)
    setLastDraftedAnchorId(null)
    void loadConversation(selectedCustomerId)
    void loadHistory(selectedCustomerId)
  }, [selectedCustomerId])

  useEffect(() => {
    if (selectedCustomerId == null) return
    const id = window.setInterval(() => {
      void loadConversation(selectedCustomerId, false)
    }, 15000)
    return () => window.clearInterval(id)
  }, [selectedCustomerId])

  useEffect(() => {
    if (!conversation || selectedCustomerId == null) return
    if (draftBatch != null || generating || attended != null) return
    if (lastDraftedAnchorId == null) return
    const latestCustomerMessage = [...conversation.messages].reverse().find((m) => m.role === 'customer')
    if (!latestCustomerMessage || latestCustomerMessage.message_id <= lastDraftedAnchorId) return
    setLastDraftedAnchorId(latestCustomerMessage.message_id)
    void generateDraft()
  }, [conversation, draftBatch, generating, attended, lastDraftedAnchorId, selectedCustomerId])

  async function loadCandidates(silent = false) {
    if (!silent) setError(null)
    try {
      const search = new URLSearchParams()
      search.set('limit', '50')
      if (filterChannel.trim()) search.set('channel', filterChannel.trim())
      if (searchQuery.trim()) search.set('query', searchQuery.trim())
      const rows = await getJson<LiveReplyCandidate[]>(`/v1/admin/live-reply/candidates?${search.toString()}`)
      setCandidates(rows)
      if (!silent) log('ok', `loaded ${rows.length} unattended candidates`)
    } catch (err) {
      if (!silent) setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function loadConversation(customerId: number, loud = true) {
    try {
      const data = await getJson<LiveReplyConversationResponse>(`/v1/admin/live-reply/conversations/${customerId}?limit=80`)
      setConversation(data)
      if (loud) log('ok', `opened customer #${customerId}`)
    } catch (err) {
      if (loud) setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function loadHistory(customerId: number) {
    try {
      setHistory(await getJson<LiveReplyHistoryEntry[]>(`/v1/admin/live-reply/history?customer_id=${customerId}&limit=20`))
    } catch (err) {
      console.warn('failed to load history', err)
    }
  }

  async function loadSentRows() {
    try {
      const search = new URLSearchParams()
      search.set('limit', '80')
      if (sentFilter.trim()) search.set('verification_status', sentFilter.trim())
      const rows = await getJson<LiveReplySentEntry[]>(`/v1/admin/live-reply/sent?${search.toString()}`)
      setSentRows(rows)
      if (rows.length > 0 && selectedSentId == null) setSelectedSentId(rows[0].id)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function loadSentDetail(liveReplyId: number) {
    try {
      const detail = await getJson<LiveReplySentDetail>(`/v1/admin/live-reply/sent/${liveReplyId}`)
      setSentDetail(detail)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function loadServedRows() {
    try {
      const search = new URLSearchParams()
      search.set('limit', '80')
      if (servedQuery.trim()) search.set('query', servedQuery.trim())
      const rows = await getJson<LiveReplyServedChatSummary[]>(`/v1/admin/live-reply/served-chats?${search.toString()}`)
      setServedRows(rows)
      if (rows.length > 0 && selectedServedCustomerId == null) setSelectedServedCustomerId(rows[0].chatapp_customer_id)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function loadServedDetail(customerId: number) {
    try {
      const detail = await getJson<LiveReplyServedChatDetail>(`/v1/admin/live-reply/served-chats/${customerId}`)
      setServedDetail(detail)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  useEffect(() => {
    if (selectedSentId != null) void loadSentDetail(selectedSentId)
  }, [selectedSentId])

  useEffect(() => {
    setServedDraftBatch(null)
    setServedEditedTexts({})
    setServedAttended(null)
    if (selectedServedCustomerId != null) void loadServedDetail(selectedServedCustomerId)
  }, [selectedServedCustomerId])

  function handleAttendedFromError(err: unknown): boolean {
    const detail = extractHumanReplied(err)
    if (!detail) return false
    setAttended(detail)
    setDraftBatch(null)
    setEditedTexts({})
    log('warn', `human already replied to anchor #${detail.anchor_message_id || '-'}`)
    if (selectedCustomerId != null) {
      setCandidates((rows) => rows.filter((row) => row.customer_id !== selectedCustomerId))
      void loadConversation(selectedCustomerId, false)
    }
    return true
  }

  async function generateDraft() {
    if (selectedCustomerId == null) return
    setGenerating(true)
    setError(null)
    setAttended(null)
    log('info', `generate customer #${selectedCustomerId}`)
    try {
      const rawData = await postJson<LiveReplyDraftBatchResponse | LiveReplyDraftResponse>(
        '/v1/admin/live-reply/draft',
        { customer_id: selectedCustomerId },
      )
      const data = normalizeDraftBatch(rawData)
      setDraftBatch(data)
      setEditedTexts(Object.fromEntries(data.drafts.map((item) => [item.live_reply_id, item.draft_text || ''])))
      if (data.anchor_message_id != null) setLastDraftedAnchorId(data.anchor_message_id)
      void loadHistory(selectedCustomerId)
      log('ok', `generated ${data.drafts.length} draft(s)`)
      toast.success(data.drafts.length > 1 ? 'Drafts generated' : 'Draft generated')
    } catch (err) {
      if (!handleAttendedFromError(err)) {
        setError(err instanceof Error ? err.message : String(err))
        log('error', 'draft generation failed')
      }
    } finally {
      setGenerating(false)
    }
  }

  async function sendReply(draft: LiveReplyDraftResponse) {
    const text = (editedTexts[draft.live_reply_id] || '').trim()
    if (!text) return
    setSendingDraftId(draft.live_reply_id)
    setError(null)
    log('info', `send live_reply_id=${draft.live_reply_id}`)
    try {
      const result = await postJson<LiveReplySendResponse>('/v1/admin/live-reply/send', {
        live_reply_id: draft.live_reply_id,
        text,
      })
      toast.success('Reply sent')
      log('ok', `sent live_reply_id=${draft.live_reply_id}`)
      setDraftBatch((batch) => (
        batch
          ? {
              ...batch,
              drafts: batch.drafts.map((item) => (
                item.live_reply_id === draft.live_reply_id ? { ...item, status: result.status } : item
              )),
            }
          : batch
      ))
      setEditedTexts((values) => ({ ...values, [draft.live_reply_id]: '' }))
      if (selectedCustomerId != null) {
        void loadConversation(selectedCustomerId, false)
        void loadHistory(selectedCustomerId)
      }
      void loadSentRows()
      void loadServedRows()
      const remainingDrafts = draftBatch?.drafts?.filter((item) => item.live_reply_id !== draft.live_reply_id && item.status !== 'sent')
      if (!remainingDrafts || remainingDrafts.length === 0) {
        setCandidates((rows) => rows.filter((row) => row.customer_id !== selectedCustomerId))
      }
    } catch (err) {
      if (!handleAttendedFromError(err)) {
        setError(err instanceof Error ? err.message : String(err))
        log('error', 'send failed')
      }
    } finally {
      setSendingDraftId(null)
    }
  }

  async function generateServedDraft() {
    if (!servedDetail) return
    const anchor = latestCustomerMessage(servedDetail.conversation.messages)
    if (!anchor || !servedDetail.summary.has_customer_reply_after_latest_agent) return
    setServedGenerating(true)
    setError(null)
    setServedAttended(null)
    log('info', `served generate customer #${servedDetail.summary.chatapp_customer_id}`)
    try {
      const rawData = await postJson<LiveReplyDraftBatchResponse | LiveReplyDraftResponse>(
        '/v1/admin/live-reply/draft',
        {
          customer_id: servedDetail.summary.chatapp_customer_id,
          anchor_message_id: anchor.message_id,
        },
      )
      const data = normalizeDraftBatch(rawData)
      setServedDraftBatch(data)
      setServedEditedTexts(Object.fromEntries(data.drafts.map((item) => [item.live_reply_id, item.draft_text || ''])))
      log('ok', `served generated ${data.drafts.length} draft(s)`)
      toast.success(data.drafts.length > 1 ? 'Drafts generated' : 'Draft generated')
    } catch (err) {
      const detail = extractHumanReplied(err)
      if (detail) {
        setServedAttended(detail)
        setServedDraftBatch(null)
        setServedEditedTexts({})
        log('warn', `human already replied to served anchor #${detail.anchor_message_id || '-'}`)
        if (selectedServedCustomerId != null) void loadServedDetail(selectedServedCustomerId)
      } else {
        setError(err instanceof Error ? err.message : String(err))
        log('error', 'served draft generation failed')
      }
    } finally {
      setServedGenerating(false)
    }
  }

  async function sendServedReply(draft: LiveReplyDraftResponse) {
    const text = (servedEditedTexts[draft.live_reply_id] || '').trim()
    if (!text) return
    setServedSendingDraftId(draft.live_reply_id)
    setError(null)
    log('info', `served send live_reply_id=${draft.live_reply_id}`)
    try {
      const result = await postJson<LiveReplySendResponse>('/v1/admin/live-reply/send', {
        live_reply_id: draft.live_reply_id,
        text,
      })
      toast.success('Reply sent')
      log('ok', `served sent live_reply_id=${draft.live_reply_id}`)
      setServedDraftBatch((batch) => (
        batch
          ? {
              ...batch,
              drafts: batch.drafts.map((item) => (
                item.live_reply_id === draft.live_reply_id ? { ...item, status: result.status } : item
              )),
            }
          : batch
      ))
      setServedEditedTexts((values) => ({ ...values, [draft.live_reply_id]: '' }))
      if (selectedServedCustomerId != null) {
        void loadServedDetail(selectedServedCustomerId)
      }
      void loadServedRows()
      void loadSentRows()
    } catch (err) {
      const detail = extractHumanReplied(err)
      if (detail) {
        setServedAttended(detail)
        log('warn', `human already replied to served anchor #${detail.anchor_message_id || '-'}`)
        if (selectedServedCustomerId != null) void loadServedDetail(selectedServedCustomerId)
      } else {
        setError(err instanceof Error ? err.message : String(err))
        log('error', 'served send failed')
      }
    } finally {
      setServedSendingDraftId(null)
    }
  }

  async function submitVerification(row: LiveReplySentEntry, verificationStatus: LiveReplyVerificationStatus) {
    try {
      await patchJson(`/v1/admin/live-reply/${row.id}/verification`, {
        verification_status: verificationStatus,
        verification_notes: row.verification_notes || null,
      })
      log('ok', `verification saved ${verificationStatus}`)
      void loadSentRows()
      if (selectedSentId === row.id) void loadSentDetail(row.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const selectedCandidate = useMemo(
    () => candidates.find((row) => row.customer_id === selectedCustomerId) || null,
    [candidates, selectedCustomerId],
  )
  const draftItems = draftBatch?.drafts ?? []
  const primaryDraft = draftItems[0] || null
  const context = ((draftBatch?.context_discovery || primaryDraft?.context_discovery) as ContextDebug | null) || null
  const snapshotMessages = (sentDetail?.sent_snapshot?.messages || []) as ChatMessage[]

  return (
    <AppShell>
      <div className="h-full overflow-hidden font-mono" style={{ background: SOLAR.base03, color: SOLAR.base0 }}>
        <div className="flex h-full flex-col">
          <header className="border-b px-4 py-3" style={{ borderColor: SOLAR.base02 }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => navigate('/admin')} className="text-xs uppercase tracking-[0.18em]" style={{ color: SOLAR.base01 }}>
                  ../admin
                </button>
                <div className="text-sm uppercase tracking-[0.24em]" style={{ color: SOLAR.base2 }}>
                  crm-agent://live-reply
                </div>
              </div>
              <nav className="flex border" style={{ borderColor: SOLAR.base02 }}>
                {(['console', 'sent', 'served'] as WorkbenchTab[]).map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setTab(item)}
                    className="px-4 py-2 text-xs uppercase tracking-[0.14em]"
                    style={{
                      background: tab === item ? SOLAR.base02 : 'transparent',
                      color: tab === item ? SOLAR.base2 : SOLAR.base0,
                      borderRight: item === 'served' ? 'none' : `1px solid ${SOLAR.base02}`,
                    }}
                  >
                    {item}
                  </button>
                ))}
              </nav>
            </div>
          </header>

          {error && (
            <div className="border-b px-4 py-2 text-sm" style={{ borderColor: SOLAR.red, color: SOLAR.red, background: '#2b1115' }}>
              {error}
            </div>
          )}

          {tab === 'console' && (
            <div className="grid min-h-0 flex-1 grid-cols-[320px_minmax(0,1fr)_390px]">
              <aside className="min-h-0 border-r" style={{ borderColor: SOLAR.base02 }}>
                <PaneHeader title="QUEUE" count={candidates.length} />
                <div className="grid grid-cols-2 gap-2 border-b p-3" style={{ borderColor: SOLAR.base02 }}>
                  <TerminalInput placeholder="channel" value={filterChannel} onChange={setFilterChannel} />
                  <TerminalInput placeholder="search/id" value={searchQuery} onChange={setSearchQuery} onEnter={() => void loadCandidates()} />
                  <div className="col-span-2">
                    <CommandButton onClick={() => void loadCandidates()}>refresh</CommandButton>
                  </div>
                </div>
                <div className="max-h-full overflow-y-auto">
                  {candidates.map((row) => (
                    <button
                      key={row.customer_id}
                      type="button"
                      onClick={() => setSelectedCustomerId(row.customer_id)}
                      className="w-full border-b px-3 py-3 text-left"
                      style={{
                        borderColor: SOLAR.base02,
                        background: selectedCustomerId === row.customer_id ? SOLAR.base02 : 'transparent',
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="truncate text-sm" style={{ color: SOLAR.base2 }}>{row.name || `#${row.customer_id}`}</span>
                        <span className="text-[10px] uppercase" style={{ color: SOLAR.yellow }}>{row.channel}</span>
                      </div>
                      <div className="mt-1 text-xs" style={{ color: SOLAR.base01 }}>#{row.customer_id} · {formatTs(row.last_message_at)}</div>
                      <div className="mt-1 line-clamp-2 text-xs" style={{ color: SOLAR.base0 }}>{row.last_message_snippet || '-'}</div>
                    </button>
                  ))}
                </div>
              </aside>

              <main className="min-h-0 overflow-y-auto p-4">
                <div className="mb-4 border-b pb-3" style={{ borderColor: SOLAR.base02 }}>
                  <TerminalLine label="$" color={SOLAR.green}>
                    {selectedCustomerId == null
                      ? 'select customer from queue'
                      : `open #${selectedCustomerId} ${conversation?.customer?.name || selectedCandidate?.name || ''}`}
                  </TerminalLine>
                  {logs.map((line, index) => (
                    <TerminalLine
                      key={`${line.text}-${index}`}
                      label={line.kind}
                      color={line.kind === 'error' ? SOLAR.red : line.kind === 'warn' ? SOLAR.yellow : line.kind === 'ok' ? SOLAR.green : SOLAR.blue}
                    >
                      {line.text}
                    </TerminalLine>
                  ))}
                </div>
                {attended && (
                  <div className="mb-4 border p-3 text-sm" style={{ borderColor: SOLAR.yellow, color: SOLAR.yellow, background: SOLAR.base02 }}>
                    human already replied: {attended.agent_reply.from_name || attended.agent_reply.from_id} · {attended.agent_reply.body}
                  </div>
                )}
                <Transcript messages={conversation?.messages || []} />
                {draftBatch && (
                  <div className="mt-5 border-t pt-4" style={{ borderColor: SOLAR.base02 }}>
                    <TerminalLine label="DRAFT" color={SOLAR.yellow}>{draftBatch.customer_text}</TerminalLine>
                    {draftItems.map((item, index) => (
                      <div key={item.live_reply_id} className="mt-4 border p-3" style={{ borderColor: SOLAR.base02, background: SOLAR.base02 }}>
                        <div className="mb-2 flex items-center justify-between text-xs" style={{ color: SOLAR.base1 }}>
                          <span>reply {index + 1}/{draftItems.length} · live_reply_id={item.live_reply_id}</span>
                          <span>{formatMoney(item.cost_usd)}</span>
                        </div>
                        <TextArea
                          value={editedTexts[item.live_reply_id] || ''}
                          onChange={(e) => setEditedTexts((values) => ({ ...values, [item.live_reply_id]: e.target.value }))}
                          rows={7}
                          className="border text-sm"
                          style={{ background: SOLAR.base03, borderColor: SOLAR.base01, color: SOLAR.base2, borderRadius: 2 }}
                          disabled={item.status === 'sent'}
                        />
                        <div className="mt-3 flex flex-wrap gap-2">
                          <CommandButton
                            tone="ok"
                            disabled={sendingDraftId != null || item.status === 'sent' || !(editedTexts[item.live_reply_id] || '').trim()}
                            onClick={() => void sendReply(item)}
                          >
                            {item.status === 'sent' ? 'sent' : 'send'}
                          </CommandButton>
                          <CommandButton onClick={() => setEditedTexts((values) => ({ ...values, [item.live_reply_id]: item.draft_text || '' }))}>reset</CommandButton>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </main>

              <aside className="min-h-0 overflow-y-auto border-l" style={{ borderColor: SOLAR.base02 }}>
                <PaneHeader title="COMMANDS" count={draftItems.length} />
                <div className="space-y-3 p-3">
                  <CommandButton disabled={selectedCustomerId == null || generating || attended != null} onClick={() => void generateDraft()}>
                    {generating ? 'generating' : 'generate'}
                  </CommandButton>
                  <CommandButton onClick={() => setDraftBatch(null)} disabled={!draftBatch}>clear</CommandButton>
                  <CommandButton onClick={() => context && log('info', context.context_summary || 'context loaded')} disabled={!context}>context</CommandButton>
                  <CommandButton onClick={() => log('info', JSON.stringify(context?.tool_trace_summary || { tool_count: 0 }))} disabled={!context}>tools</CommandButton>
                  <CommandButton onClick={() => selectedCustomerId != null && void loadConversation(selectedCustomerId)}>history</CommandButton>
                </div>
                <SideBlock title="Context">
                  {context ? (
                    <div className="space-y-2 text-xs">
                      <KV label="source" value={String(context.source || '-')} />
                      <KV label="intent" value={String(context.intent || '-')} />
                      <KV label="status" value={String(context.resolution_status || '-')} />
                      <KV label="msgs" value={`${context.discovered_message_count ?? 0}/${context.total_examined_message_count ?? 0}`} />
                      <p className="pt-2 leading-relaxed" style={{ color: SOLAR.base1 }}>{context.context_summary || ''}</p>
                    </div>
                  ) : (
                    <span className="text-xs" style={{ color: SOLAR.base01 }}>generate a draft to inspect context</span>
                  )}
                </SideBlock>
                <SideBlock title="Local history">
                  <div className="space-y-2">
                    {history.map((row) => (
                      <div key={row.id} className="border p-2 text-xs" style={{ borderColor: SOLAR.base02 }}>
                        <div style={{ color: SOLAR.base01 }}>{formatTs(row.sent_at || row.created_at)}</div>
                        <div className="mt-1 line-clamp-3" style={{ color: SOLAR.base1 }}>{row.sent_text || row.draft_text || row.error_message || '-'}</div>
                      </div>
                    ))}
                  </div>
                </SideBlock>
              </aside>
            </div>
          )}

          {tab === 'sent' && (
            <LedgerView
              rows={sentRows}
              selectedId={selectedSentId}
              onSelect={setSelectedSentId}
              detail={sentDetail}
              filter={sentFilter}
              setFilter={setSentFilter}
              reload={() => void loadSentRows()}
              snapshotMessages={snapshotMessages}
              submitVerification={submitVerification}
            />
          )}

          {tab === 'served' && (
            <ServedView
              rows={servedRows}
              selectedCustomerId={selectedServedCustomerId}
              onSelect={setSelectedServedCustomerId}
              detail={servedDetail}
              query={servedQuery}
              setQuery={setServedQuery}
              reload={() => void loadServedRows()}
              draftBatch={servedDraftBatch}
              editedTexts={servedEditedTexts}
              setEditedTexts={setServedEditedTexts}
              generating={servedGenerating}
              sendingDraftId={servedSendingDraftId}
              attended={servedAttended}
              generateDraft={() => void generateServedDraft()}
              sendReply={(draft) => void sendServedReply(draft)}
            />
          )}
        </div>
      </div>
    </AppShell>
  )
}

function PaneHeader({ title, count }: { title: string; count?: number }) {
  return (
    <div className="flex items-center justify-between border-b px-3 py-2" style={{ borderColor: SOLAR.base02, color: SOLAR.base1 }}>
      <span className="text-xs uppercase tracking-[0.18em]">{title}</span>
      {count != null && <span className="text-xs" style={{ color: SOLAR.base01 }}>{count}</span>}
    </div>
  )
}

function TerminalInput({ value, onChange, placeholder, onEnter }: { value: string; onChange: (value: string) => void; placeholder: string; onEnter?: () => void }) {
  return (
    <input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') onEnter?.() }}
      className="w-full border px-2 py-2 text-xs outline-none"
      style={{ background: SOLAR.base03, borderColor: SOLAR.base02, color: SOLAR.base2, borderRadius: 2 }}
    />
  )
}

function SideBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t p-3" style={{ borderColor: SOLAR.base02 }}>
      <div className="mb-2 text-[10px] uppercase tracking-[0.16em]" style={{ color: SOLAR.base01 }}>{title}</div>
      {children}
    </section>
  )
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span style={{ color: SOLAR.base01 }}>{label}</span>
      <span className="text-right" style={{ color: SOLAR.base2 }}>{value}</span>
    </div>
  )
}

function LedgerView({
  rows,
  selectedId,
  onSelect,
  detail,
  filter,
  setFilter,
  reload,
  snapshotMessages,
  submitVerification,
}: {
  rows: LiveReplySentEntry[]
  selectedId: number | null
  onSelect: (id: number) => void
  detail: LiveReplySentDetail | null
  filter: string
  setFilter: (value: string) => void
  reload: () => void
  snapshotMessages: ChatMessage[]
  submitVerification: (row: LiveReplySentEntry, status: LiveReplyVerificationStatus) => void
}) {
  return (
    <div className="grid min-h-0 flex-1 grid-cols-[360px_minmax(0,1fr)_430px]">
      <aside className="min-h-0 border-r" style={{ borderColor: SOLAR.base02 }}>
        <PaneHeader title="SENT LEDGER" count={rows.length} />
        <div className="flex gap-2 border-b p-3" style={{ borderColor: SOLAR.base02 }}>
          <TerminalInput value={filter} onChange={setFilter} placeholder="status filter" onEnter={reload} />
          <CommandButton onClick={reload}>load</CommandButton>
        </div>
        <div className="overflow-y-auto">
          {rows.map((row) => (
            <button
              key={row.id}
              type="button"
              onClick={() => onSelect(row.id)}
              className="w-full border-b px-3 py-3 text-left"
              style={{ borderColor: SOLAR.base02, background: selectedId === row.id ? SOLAR.base02 : 'transparent' }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm" style={{ color: SOLAR.base2 }}>{row.customer_name || `#${row.chatapp_customer_id}`}</span>
                <StatusChip color={verificationColor(row.verification_status)}>{row.verification_status || 'unreviewed'}</StatusChip>
              </div>
              <div className="mt-1 text-xs" style={{ color: SOLAR.base01 }}>sent {formatTs(row.sent_at)}</div>
              <div className="mt-1 line-clamp-2 text-xs" style={{ color: SOLAR.base0 }}>{short(row.sent_text)}</div>
            </button>
          ))}
        </div>
      </aside>
      <main className="min-h-0 overflow-y-auto p-4">
        <TerminalLine label="$" color={SOLAR.green}>sent detail {detail ? `#${detail.id}` : 'not selected'}</TerminalLine>
        {detail && (
          <div className="mt-4 space-y-4">
            <SideBlock title="Customer comment">
              <pre className="whitespace-pre-wrap text-sm" style={{ color: SOLAR.base2 }}>{detail.customer_text}</pre>
            </SideBlock>
            <SideBlock title="Sent reply">
              <pre className="whitespace-pre-wrap text-sm" style={{ color: SOLAR.green }}>{detail.sent_text}</pre>
            </SideBlock>
            <SideBlock title="Evaluation">
              <div className="space-y-2 text-xs">
                <KV label="status" value={detail.verification_status || 'unreviewed'} />
                <KV label="verifier" value={detail.verified_by_email || '-'} />
                <KV label="notes" value={detail.verification_notes || '-'} />
                <div className="flex gap-2 pt-2">
                  <CommandButton tone="ok" onClick={() => submitVerification(detail, 'approved')}>approve</CommandButton>
                  <CommandButton onClick={() => submitVerification(detail, 'needs_review')}>review</CommandButton>
                  <CommandButton tone="danger" onClick={() => submitVerification(detail, 'bad')}>bad</CommandButton>
                </div>
              </div>
            </SideBlock>
          </div>
        )}
      </main>
      <aside className="min-h-0 overflow-y-auto border-l" style={{ borderColor: SOLAR.base02 }}>
        <PaneHeader title="SEND SNAPSHOT" count={snapshotMessages.length} />
        <div className="p-3">
          <Transcript messages={snapshotMessages} />
        </div>
      </aside>
    </div>
  )
}

function ServedView({
  rows,
  selectedCustomerId,
  onSelect,
  detail,
  query,
  setQuery,
  reload,
  draftBatch,
  editedTexts,
  setEditedTexts,
  generating,
  sendingDraftId,
  attended,
  generateDraft,
  sendReply,
}: {
  rows: LiveReplyServedChatSummary[]
  selectedCustomerId: number | null
  onSelect: (id: number) => void
  detail: LiveReplyServedChatDetail | null
  query: string
  setQuery: (value: string) => void
  reload: () => void
  draftBatch: LiveReplyDraftBatchResponse | null
  editedTexts: Record<number, string>
  setEditedTexts: Dispatch<SetStateAction<Record<number, string>>>
  generating: boolean
  sendingDraftId: number | null
  attended: LiveReplyHumanRepliedDetail | null
  generateDraft: () => void
  sendReply: (draft: LiveReplyDraftResponse) => void
}) {
  const latestCustomer = latestCustomerMessage(detail?.conversation.messages)
  const canReplyAgain = Boolean(detail?.summary.has_customer_reply_after_latest_agent && latestCustomer)
  const draftItems = draftBatch?.drafts ?? []

  return (
    <div className="grid min-h-0 flex-1 grid-cols-[360px_minmax(0,1fr)_430px]">
      <aside className="min-h-0 border-r" style={{ borderColor: SOLAR.base02 }}>
        <PaneHeader title="SERVED CHATS" count={rows.length} />
        <div className="flex gap-2 border-b p-3" style={{ borderColor: SOLAR.base02 }}>
          <TerminalInput value={query} onChange={setQuery} placeholder="name/id" onEnter={reload} />
          <CommandButton onClick={reload}>load</CommandButton>
        </div>
        <div className="overflow-y-auto">
          {rows.map((row) => (
            <button
              key={row.chatapp_customer_id}
              type="button"
              onClick={() => onSelect(row.chatapp_customer_id)}
              className="w-full border-b px-3 py-3 text-left"
              style={{ borderColor: SOLAR.base02, background: selectedCustomerId === row.chatapp_customer_id ? SOLAR.base02 : 'transparent' }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm" style={{ color: SOLAR.base2 }}>{row.customer_name || `#${row.chatapp_customer_id}`}</span>
                <StatusChip color={row.has_customer_reply_after_latest_agent ? SOLAR.yellow : SOLAR.green}>
                  {row.has_customer_reply_after_latest_agent ? 'reply' : 'quiet'}
                </StatusChip>
              </div>
              <div className="mt-1 text-xs" style={{ color: SOLAR.base01 }}>
                {row.sent_count} sent · latest {formatTs(row.latest_sent_at)}
              </div>
              <div className="mt-1 line-clamp-2 text-xs" style={{ color: SOLAR.base0 }}>{row.last_message_snippet || '-'}</div>
            </button>
          ))}
        </div>
      </aside>
      <main className="min-h-0 overflow-y-auto p-4">
        <div className="mb-4 border-b pb-3" style={{ borderColor: SOLAR.base02 }}>
          <TerminalLine label="$" color={SOLAR.green}>served continuity {detail ? `#${detail.summary.chatapp_customer_id}` : 'not selected'}</TerminalLine>
          {detail && (
            <TerminalLine label="state" color={canReplyAgain ? SOLAR.yellow : SOLAR.green}>
              {canReplyAgain
                ? `new customer reply at #${latestCustomer?.message_id}`
                : 'no customer reply after latest agent response'}
            </TerminalLine>
          )}
          {attended && (
            <div className="mt-3 border p-3 text-sm" style={{ borderColor: SOLAR.yellow, color: SOLAR.yellow, background: SOLAR.base02 }}>
              human already replied: {attended.agent_reply.from_name || attended.agent_reply.from_id} · {attended.agent_reply.body}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <CommandButton
              tone="ok"
              disabled={!canReplyAgain || generating}
              onClick={generateDraft}
            >
              {generating ? 'generating' : 'reply again'}
            </CommandButton>
            <CommandButton onClick={reload}>refresh</CommandButton>
          </div>
        </div>
        <div className="mt-4">
          <Transcript messages={detail?.conversation.messages || []} />
        </div>
        {draftBatch && (
          <div className="mt-5 border-t pt-4" style={{ borderColor: SOLAR.base02 }}>
            <TerminalLine label="DRAFT" color={SOLAR.yellow}>{draftBatch.customer_text}</TerminalLine>
            {draftItems.map((item, index) => (
              <div key={item.live_reply_id} className="mt-4 border p-3" style={{ borderColor: SOLAR.base02, background: SOLAR.base02 }}>
                <div className="mb-2 flex items-center justify-between text-xs" style={{ color: SOLAR.base1 }}>
                  <span>reply {index + 1}/{draftItems.length} · live_reply_id={item.live_reply_id}</span>
                  <span>{formatMoney(item.cost_usd)}</span>
                </div>
                <TextArea
                  value={editedTexts[item.live_reply_id] || ''}
                  onChange={(e) => setEditedTexts((values) => ({ ...values, [item.live_reply_id]: e.target.value }))}
                  rows={7}
                  className="border text-sm"
                  style={{ background: SOLAR.base03, borderColor: SOLAR.base01, color: SOLAR.base2, borderRadius: 2 }}
                  disabled={item.status === 'sent'}
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <CommandButton
                    tone="ok"
                    disabled={sendingDraftId != null || item.status === 'sent' || !(editedTexts[item.live_reply_id] || '').trim()}
                    onClick={() => sendReply(item)}
                  >
                    {item.status === 'sent' ? 'sent' : 'send'}
                  </CommandButton>
                  <CommandButton onClick={() => setEditedTexts((values) => ({ ...values, [item.live_reply_id]: item.draft_text || '' }))}>reset</CommandButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <aside className="min-h-0 overflow-y-auto border-l" style={{ borderColor: SOLAR.base02 }}>
        <PaneHeader title="AGENT HISTORY" count={detail?.sent_replies.length || 0} />
        <div className="space-y-3 p-3">
          {detail?.sent_replies.map((row) => (
            <div key={row.id} className="border p-3 text-xs" style={{ borderColor: SOLAR.base02 }}>
              <div className="flex items-center justify-between gap-2">
                <span style={{ color: SOLAR.base01 }}>{formatTs(row.sent_at)}</span>
                <StatusChip color={verificationColor(row.verification_status)}>{row.verification_status || 'unreviewed'}</StatusChip>
              </div>
              <div className="mt-2 whitespace-pre-wrap" style={{ color: SOLAR.green }}>{row.sent_text}</div>
              {row.verification_notes && <div className="mt-2" style={{ color: SOLAR.yellow }}>note: {row.verification_notes}</div>}
            </div>
          ))}
        </div>
      </aside>
    </div>
  )
}
